import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'

/**
 * Keeps a create form's answers in this browser while someone fills it in, so
 * a refresh, a closed tab or a wrong click doesn't lose ten minutes of typing.
 * Coming back offers the draft; creating the record deletes it. Files are not
 * kept: a browser can't store them.
 */

const PREFIX = 'helm:draft:'
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const SAVE_DELAY_MS = 400

export interface SavedDraft {
  savedAt: string
  values: Record<string, unknown>
}

export function readDraft(key: string, now = Date.now()): SavedDraft | null {
  try {
    const raw = window.localStorage.getItem(PREFIX + key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SavedDraft
    const age = now - Date.parse(parsed.savedAt)
    if (!parsed.values || typeof parsed.values !== 'object' || !(age >= 0 && age < MAX_AGE_MS)) {
      window.localStorage.removeItem(PREFIX + key)
      return null
    }
    return parsed
  } catch {
    // Private windows and blocked storage throw; a corrupt draft is dropped.
    return null
  }
}

function writeDraft(key: string, values: Record<string, unknown>) {
  try {
    window.localStorage.setItem(
      PREFIX + key,
      JSON.stringify({ savedAt: new Date().toISOString(), values } satisfies SavedDraft),
    )
  } catch {
    // Storage full or blocked: the form still works, it just isn't kept.
  }
}

function removeDraft(key: string) {
  try {
    window.localStorage.removeItem(PREFIX + key)
  } catch {
    // Nothing to do.
  }
}

type DraftState = 'NONE' | 'OFFERED' | 'RESTORED' | 'DISCARDED'

export function useFormDraft(key: string) {
  const [saved] = useState(() => readDraft(key))
  const [state, setState] = useState<DraftState>(saved ? 'OFFERED' : 'NONE')
  const values = useRef<Record<string, unknown>>({})
  const initial = useRef<Record<string, unknown>>({})
  const setters = useRef<Record<string, (value: unknown) => void>>({})
  const lastWritten = useRef<string | null>(null)
  const [restoredStep, setRestoredStep] = useState<string | null>(null)

  // After every render, a short pause, then keep the draft current if someone
  // has changed something. Fields register in their own effects, so the check
  // runs in the timer, after they have. While a saved draft is still on offer
  // nothing is written, so it is not overwritten before the person chooses.
  useEffect(() => {
    if (state === 'OFFERED') return
    const timer = window.setTimeout(() => {
      const fields = Object.keys(values.current).filter((name) => name !== '__step')
      const changed = fields.some(
        (name) => JSON.stringify(values.current[name]) !== JSON.stringify(initial.current[name]),
      )
      const serialised = JSON.stringify(values.current)
      if (!changed || serialised === lastWritten.current) return
      writeDraft(key, values.current)
      lastWritten.current = serialised
    }, SAVE_DELAY_MS)
    return () => window.clearTimeout(timer)
  })

  return {
    /** When a saved draft is waiting for a decision, when it was saved. */
    offeredAt: state === 'OFFERED' && saved ? saved.savedAt : null,
    offeredValues: state === 'OFFERED' && saved ? saved.values : null,
    /** The step the restored draft was on, for the wizard to reopen. */
    restoredStep,
    restore() {
      if (!saved) return
      for (const [name, value] of Object.entries(saved.values)) {
        setters.current[name]?.(value)
      }
      setRestoredStep(typeof saved.values.__step === 'string' ? saved.values.__step : null)
      setState('RESTORED')
    },
    discard() {
      removeDraft(key)
      setState('DISCARDED')
    },
    /** After the record is created. */
    clear() {
      removeDraft(key)
      lastWritten.current = null
    },
    /** Records a value that isn't form state, such as the current step. */
    track(name: string, value: unknown) {
      values.current[name] = value
    },
    register<T>(name: string, value: T, initialValue: T, setValue: Dispatch<SetStateAction<T>>) {
      values.current[name] = value
      if (!(name in initial.current)) initial.current[name] = initialValue
      setters.current[name] = setValue as (value: unknown) => void
    },
  }
}

export type FormDraft = ReturnType<typeof useFormDraft>

/** `useState`, remembered in the form's draft under `name`. */
export function useDraftState<T>(
  draft: FormDraft,
  name: string,
  initialValue: T | (() => T),
): [T, Dispatch<SetStateAction<T>>] {
  const [first] = useState(initialValue)
  const [value, setValue] = useState<T>(first)
  const { register } = draft
  useEffect(() => {
    register(name, value, first, setValue)
  }, [register, name, value, first, setValue])
  return [value, setValue]
}
